import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { enterAssignment, getAssignment } from '../lib/api';
import { DraftSpace } from './DraftSpace';
import { DraftyMascot } from './DraftyMascot';
import Eyebrow from './Eyebrow';

/**
 * WriteSpace — the direct-to-editor route (/write/:assignmentId).
 *
 * One entry call auto-enrolls the student in the assignment's class and
 * finds-or-creates their draft, then the editor renders immediately with
 * the coach panel ready. This is where assignment links (e.g. from Google
 * Classroom) land — no dashboard detour.
 */
const WriteSpace = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();

  // "See what students see": teachers open their own assignment with
  // ?preview=1 and get the real student editor, persistence disabled.
  const isPreview = new URLSearchParams(window.location.search).get('preview') === '1';

  const [assignment, setAssignment] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const enter = async () => {
      try {
        // Teacher preview ("See what students see"): load the assignment
        // directly and drive the student view with a synthetic unsaved draft.
        // Do NOT call enterAssignment — that is the student enrollment path and
        // 403s for a teacher who isn't on their own class roster (F1).
        if (isPreview) {
          const a = await getAssignment(assignmentId);
          if (cancelled) return;
          setAssignment(a);
          setDraft({ id: null, content: '', stage: 'prewrite', status: 'draft' });
          return;
        }
        const data = await enterAssignment(assignmentId);
        if (cancelled) return;
        setAssignment(data.assignment);
        if (data.draft) {
          setDraft(data.draft);
        } else {
          // Teachers (no draft) preview via the dashboard instead
          navigate('/dashboard', { replace: true });
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403 && e?.response?.data?.detail?.code) {
          // Roster-or-code gate — the join page owns the code form
          navigate(`/join/assignment/${assignmentId}`, { replace: true });
          return;
        }
        if (status === 410) {
          setError('This assignment is closed. Ask your teacher for a new link.');
        } else if (status === 404) {
          setError('Assignment not found. Check the link with your teacher.');
        } else {
          setError('Something went wrong opening your assignment. Try again in a moment.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    enter();
    return () => { cancelled = true; };
  }, [assignmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-flow-blue border-t-transparent mx-auto mb-4" />
          <p className="text-slate-400">Opening your assignment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <DraftyMascot size="lg" mood="thinking" className="mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Can't Open Assignment</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/dashboard" className="text-flow-blue-hover hover:underline text-sm">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!assignment || !draft) return null;

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="h-screen flex flex-col p-6">
        {/* Header — mirrors the dashboard editor header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
              aria-label="Back to dashboard"
            >
              <ChevronRight className="rotate-180" size={20} />
            </button>
            <div>
              <Eyebrow label="DRAFT IN PROGRESS" tone="teal" />
              <h2 className="font-heading text-xl font-semibold text-white">
                {assignment.title}
              </h2>
              <p className="text-slate-400 text-sm">
                {assignment.due_date
                  ? `Due ${new Date(assignment.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · `
                  : ''}
                {assignment.prompt?.slice(0, 100)}...
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {assignment.ccss_standards?.map((std, idx) => (
              <span key={idx} className="badge-ccss">{std}</span>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto">
          <DraftSpace
            draft={draft}
            assignment={assignment}
            onUpdate={(data) => {
              const changes = { ...data };
              if (changes.wordCount !== undefined) {
                changes.word_count = changes.wordCount;
                delete changes.wordCount;
              }
              setDraft(prev => ({ ...prev, ...changes }));
            }}
          />
        </div>
      </div>

    </div>
  );
};

export default WriteSpace;
